import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldWarning, SignOut, DotsSixVertical } from "@phosphor-icons/react";
import { getImpersonateBanner, getTenant, stopImpersonate } from "@/lib/sessionStore";

const POS_STORAGE_KEY = "agentus_impersonate_chip_pos";

/**
 * Persistent amber chip shown while a super_admin is impersonating a tenant.
 * Drag-and-drop enabled (Marcelo Omega jun/2026) — operador arrasta pra
 * qualquer canto da viewport pra não cobrir botões da UI. Posição
 * persistida em localStorage, então fica onde foi deixado entre páginas.
 *
 * Safety rules:
 *   - Original super_admin session is stashed by sessionStore.startImpersonate.
 *   - "Sair" calls /api/auth/stop-impersonate (audit log) then
 *     sessionStore.stopImpersonate restores the admin's session atomically.
 *   - If no stash exists we drop everything and bounce to admin login —
 *     never leave a dangling token.
 */
export default function ImpersonateBanner() {
  const [who, setWho] = useState(null);
  const [tenantName, setTenantName] = useState("");
  const [exiting, setExiting] = useState(false);
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(POS_STORAGE_KEY) || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved;
    } catch {} // eslint-disable-line
    // Default: bottom-right (16px margin)
    return { x: window.innerWidth - 360, y: window.innerHeight - 64 };
  });
  const dragRef = useRef(null);
  const dragState = useRef({ active: false, dx: 0, dy: 0, moved: false });

  useEffect(() => {
    const flag = getImpersonateBanner();
    if (flag) {
      setWho(flag);
      setTenantName(getTenant()?.name || "");
    }
  }, []);

  // Mantém o chip dentro da viewport quando a janela é redimensionada.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        const el = dragRef.current;
        const w = el ? el.offsetWidth : 320;
        const h = el ? el.offsetHeight : 44;
        return {
          x: Math.min(Math.max(8, p.x), window.innerWidth - w - 8),
          y: Math.min(Math.max(8, p.y), window.innerHeight - h - 8),
        };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!who) return null;

  const startDrag = (clientX, clientY) => {
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      active: true, moved: false,
      dx: clientX - rect.left,
      dy: clientY - rect.top,
    };
  };

  const onMouseDown = (e) => {
    if (e.target.closest("button")) return;  // não inicia drag quando clica no botão Sair
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onTouchStart = (e) => {
    if (e.target.closest("button")) return;
    const t = e.touches[0];
    if (!t) return;
    startDrag(t.clientX, t.clientY);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onMouseUp);
  };

  const moveTo = (clientX, clientY) => {
    if (!dragState.current.active) return;
    dragState.current.moved = true;
    const el = dragRef.current;
    const w = el ? el.offsetWidth : 320;
    const h = el ? el.offsetHeight : 44;
    const nx = Math.min(Math.max(8, clientX - dragState.current.dx), window.innerWidth - w - 8);
    const ny = Math.min(Math.max(8, clientY - dragState.current.dy), window.innerHeight - h - 8);
    setPos({ x: nx, y: ny });
  };

  const onMouseMove = (e) => moveTo(e.clientX, e.clientY);
  const onTouchMove = (e) => {
    e.preventDefault();
    const t = e.touches[0];
    if (t) moveTo(t.clientX, t.clientY);
  };

  const onMouseUp = () => {
    if (dragState.current.active && dragState.current.moved) {
      try { localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos)); } catch {} // eslint-disable-line
    }
    dragState.current.active = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("touchend", onMouseUp);
  };

  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await api.post("/auth/stop-impersonate").catch((e) => {
        console.warn("[impersonate] stop-impersonate audit failed", e);
      });
      const restored = stopImpersonate();
      if (!restored) {
        window.location.href = "/admin-master/login";
        return;
      }
      toast.success("Sessão do suporte restaurada");
      window.location.href = "/admin-master/tenants";
    } catch (e) {
      toast.error("Falha ao sair da impersonação");
      setExiting(false);
    }
  };

  return (
    <>
      <div
        ref={dragRef}
        data-testid="impersonate-banner"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        className="fixed z-[100] bg-amber-400/95 backdrop-blur text-slate-900 rounded-full border-2 border-amber-500 shadow-xl animate-[slideUp_.2s_ease-out] select-none cursor-grab active:cursor-grabbing touch-none"
        style={{ left: `${pos.x}px`, top: `${pos.y}px`, animationFillMode: "both" }}
        title="Arraste pra mover"
      >
        <div className="flex items-center gap-1.5 pl-1.5 pr-1.5 py-1.5">
          <DotsSixVertical size={14} weight="bold" className="shrink-0 opacity-60" />
          <ShieldWarning size={14} weight="fill" className="shrink-0" />
          <div className="flex items-center gap-1.5 text-[12px] font-semibold">
            <span className="uppercase tracking-wider text-[9px] bg-slate-900 text-amber-400 px-1.5 py-0.5 rounded">
              Suporte
            </span>
            <span className="truncate max-w-[180px]" title={`${tenantName || "como cliente"} — ${who}`}>
              {tenantName || "como cliente"}
            </span>
          </div>
          <button
            onClick={exit}
            disabled={exiting}
            data-testid="impersonate-exit-btn"
            title="Sair da impersonação (Todas as ações são auditadas)"
            className="inline-flex items-center gap-1 bg-slate-900 text-amber-400 hover:bg-slate-800 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors disabled:opacity-70 ml-1 cursor-pointer"
          >
            <SignOut size={11} weight="bold" />
            {exiting ? "Saindo" : "Sair"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(120%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
